import { SystemAlert } from "@cms/components/data/Alert";
import { Snackbar } from "@mui/material";
import { createContext, ReactNode, useContext, useState } from "react";

type SnackbarContextType = {
    showSnackbar: (message: string, severity?: Severity) => void;
};

type Severity = "success" | "error" | "warning" | "info";

const SnackbarContext = createContext<SnackbarContextType | null>(null);

export const useSnackbar = () => {
    const ctx = useContext(SnackbarContext);
    if (!ctx) throw new Error("useSnackbar must be used within SnackbarProvider");
    return ctx;
};

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: Severity;
    }>({
        open: false,
        message: "",
        severity: "info",
    });

    const showSnackbar = (message: string, severity: Severity = "info") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <SnackbarContext.Provider value={{ showSnackbar }}>
            {children}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <SystemAlert onClose={handleClose} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </SystemAlert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};
